/**
 * ReferencePdfUpload - Admin UI for uploading/removing reference PDFs.
 *
 * Features:
 * - Upload PDF with presigned URL
 * - Preview current PDF (if exists)
 * - Remove PDF with confirmation
 * - File validation (PDF only, max 50MB)
 * - Upload progress indication
 */

import { useState, useRef } from 'react';
import { FileUp, Trash2, FileText, ExternalLink, AlertCircle, CheckCircle } from 'lucide-react';
import { Button, LoadingSpinner } from '../common';
import { orgTemplatesApi } from '../../services/api';
import {
  useUploadReferencePdf,
  useDeleteReferencePdf,
} from '../../hooks/useOrgTemplatePdf';
import { validatePdfFile } from '../../utils/validatePdfFile';

interface ReferencePdfUploadProps {
  templateId: string;
  currentPdfKey?: string;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export default function ReferencePdfUpload({
  templateId,
  currentPdfKey,
}: ReferencePdfUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const uploadMutation = useUploadReferencePdf(templateId);
  const deleteMutation = useDeleteReferencePdf(templateId);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError(null);
    setUploadSuccess(false);

    const error = validatePdfFile(file, MAX_FILE_SIZE);
    if (error) {
      setUploadError(error);
      return;
    }

    setIsUploading(true);
    uploadMutation.mutate(file, {
      onSuccess: () => {
        setUploadSuccess(true);
        setUploadError(null);
        setTimeout(() => setUploadSuccess(false), 3000);
      },
      onError: (err: Error) => {
        setUploadError(err.message);
        setUploadSuccess(false);
      },
      onSettled: () => setIsUploading(false),
    });

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleViewPdf = async () => {
    try {
      const { url } = await orgTemplatesApi.getReferencePdfUrl(templateId);
      window.open(url, '_blank');
    } catch {
      setUploadError('Failed to get PDF URL');
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate(undefined, {
      onSuccess: () => setShowDeleteConfirm(false),
      onError: (err: Error) => setUploadError(err.message),
    });
  };

  return (
    <div className="space-y-4">
      {/* Current PDF status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className={`h-5 w-5 ${currentPdfKey ? 'text-brand-600 dark:text-brand-400' : 'text-slate-400'}`} />
          <div>
            <p className="text-body-sm font-medium text-slate-900 dark:text-slate-100">
              Reference PDF
            </p>
            <p className="text-caption text-slate-500 dark:text-slate-400">
              {currentPdfKey
                ? 'PDF uploaded for clause navigation'
                : 'No reference PDF uploaded'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {currentPdfKey && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleViewPdf}
                icon={<ExternalLink className="h-4 w-4" />}
              >
                View
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                icon={<Trash2 className="h-4 w-4" />}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
              >
                Remove
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Upload section */}
      <div className="border border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-4 bg-slate-50 dark:bg-slate-800">
        <div className="flex flex-col items-center justify-center text-center">
          <FileUp className="h-8 w-8 text-slate-400 mb-2" />
          <p className="text-body-sm text-slate-600 dark:text-slate-400 mb-1">
            {currentPdfKey ? 'Replace reference PDF' : 'Upload reference PDF'}
          </p>
          <p className="text-caption text-slate-500 dark:text-slate-400 mb-3">
            PDF only, max 50MB
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileSelect}
            className="hidden"
            id="pdf-upload"
          />

          <Button
            variant="secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            icon={isUploading ? <LoadingSpinner size="sm" /> : <FileUp className="h-4 w-4" />}
          >
            {isUploading ? 'Uploading...' : 'Choose File'}
          </Button>
        </div>
      </div>

      {/* Success message */}
      {uploadSuccess && (
        <div className="flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
          <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          <p className="text-body-sm text-emerald-700 dark:text-emerald-400">
            Reference PDF uploaded successfully
          </p>
        </div>
      )}

      {/* Error message */}
      {uploadError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
          <p className="text-body-sm text-red-700 dark:text-red-400">{uploadError}</p>
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-body-sm text-amber-700 dark:text-amber-400">
            Remove reference PDF? This cannot be undone.
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleDelete}
              loading={deleteMutation.isPending}
              className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20"
            >
              Remove
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
